import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { GLTF, GLTFParser } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { ThreeMFLoader } from 'three/examples/jsm/loaders/3MFLoader.js';
import type { ModelLoaderType } from './modelFiles';
import { validateGltfBuffer, validateStlBuffer } from './validate';
import { isOcctGltf } from './brepEdges';
import { faceGeometries, meshGeometry } from './meshData';
import { creasedNormals } from './viewport';

/** The viewer's themed stand-ins for primitives whose file declares no material. */
export interface ModelMaterials {
  readonly mesh: THREE.Material;
  readonly line: THREE.LineBasicMaterial;
}

/** A Draco decoder that failed to load is not the model's fault, so its bytes are tried again. */
export class DecoderUnavailable extends Error {
  readonly reason: unknown;

  constructor(reason: unknown) {
    super('Draco decoder unavailable');
    this.reason = reason;
  }
}

// A primitive that declares no material gets GLTFLoader's shared default, which
// has no entry in the parser's associations. Our CAD previews are written that
// way, so they are themed like an STL instead of rendering as white metal.
function hasFileMaterial(
  parser: GLTFParser,
  material: THREE.Material | THREE.Material[],
): boolean {
  return Array.isArray(material) || parser.associations.get(material)?.materials !== undefined;
}

function gltfObject(gltf: GLTF, materials: ModelMaterials): THREE.Object3D {
  gltf.scene.traverse((child) => {
    // Lines are unlit, so a glTF's own material — white by default — would sit
    // invisibly on the light theme's near-white background.
    const line = child as THREE.Line;
    if (line.isLine) line.material = materials.line;
    const mesh = child as THREE.Mesh;
    if (mesh.isMesh && !hasFileMaterial(gltf.parser, mesh.material)) {
      mesh.material = materials.mesh;
    }
  });
  // glTF 2.0 mandates Y-up; rotate into the scene's Z-up frame.
  const wrapper = new THREE.Group();
  wrapper.rotation.x = Math.PI / 2;
  wrapper.userData.brepFaces = isOcctGltf(gltf.asset);
  wrapper.add(gltf.scene);
  return wrapper;
}

/**
 * Parses from the in-memory buffer. Loaders are never handed the model's URL,
 * so nothing here can re-fetch it or hit a revoked object URL.
 */
export function parseModel(
  buffer: ArrayBuffer,
  loader: ModelLoaderType,
  materials: ModelMaterials,
): Promise<THREE.Object3D> {
  return new Promise((resolve, reject) => {
    switch (loader) {
      case 'gltf': {
        if (!validateGltfBuffer(buffer)) {
          throw new Error(
            `Invalid glTF data (${buffer.byteLength} bytes). ` +
              'The file may be empty, truncated or in a non-glTF format.',
          );
        }
        // On demand: Vite rewrites the decoder's asset URLs into the chunk
        // holding them and rebuilds its source map, which the entry chunk is
        // too large for within the build's heap.
        import('./dracoLoader')
          .then(({ dracoLoader, decoderFailed }) => ({ draco: dracoLoader(), decoderFailed }))
          .then(
            ({ draco, decoderFailed }) => {
              new GLTFLoader().setDRACOLoader(draco).parse(
                buffer,
                '',
                (gltf) => resolve(gltfObject(gltf, materials)),
                (err) => reject(decoderFailed(draco) ? new DecoderUnavailable(err) : err),
              );
            },
            (err: unknown) => reject(new DecoderUnavailable(err)),
          )
          .catch(reject);
        return;
      }
      case 'stl': {
        if (!validateStlBuffer(buffer)) {
          throw new Error(
            `Invalid STL data (${buffer.byteLength} bytes). ` +
              'The file may be corrupted or in a non-STL format.',
          );
        }
        const geometry = new STLLoader().parse(buffer);
        geometry.computeVertexNormals();
        resolve(new THREE.Mesh(creasedNormals(geometry), materials.mesh));
        return;
      }
      case 'obj': {
        const obj = new OBJLoader().parse(new TextDecoder().decode(buffer));
        obj.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            (child as THREE.Mesh).material = materials.mesh;
          }
        });
        resolve(obj);
        return;
      }
      case '3mf': {
        resolve(new ThreeMFLoader().parse(buffer));
        return;
      }
      case 'mesh': {
        // Selection, snapping and brepEdges take each mesh under a brepFaces model as one B-rep face.
        const geometry = meshGeometry(buffer);
        const root = new THREE.Group();
        if (geometry.groups.length > 0) {
          root.userData.brepFaces = true;
          for (const face of faceGeometries(geometry)) root.add(new THREE.Mesh(face, materials.mesh));
          geometry.dispose();
        } else {
          root.add(new THREE.Mesh(creasedNormals(geometry), materials.mesh));
        }
        resolve(root);
        return;
      }
      default: {
        const exhaustive: never = loader;
        throw new Error(`Unsupported loader type: ${String(exhaustive)}`);
      }
    }
  });
}
